import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

import { activeNetworkSelector, userSelector } from 'redux/selectors';
import {
  useCreateUserGoogleAccountMutation,
  useDeleteUserGoogleAccountMutation,
  useLazyGetUserGoogleAccountsQuery,
} from 'services/userGoogleAccountsV2';
import { useUpdateLinkedinSyncStatusMutation, useDisconnectLinkedinSyncMutation } from 'services/userV2';
import { useGoogleSync } from 'hooks/useGoogleSync';

export const useIntegrationsPage = ({ network: networkOverride, onGoogleSyncComplete } = {}) => {
  const user = useSelector(userSelector);
  const activeNetworkFromStore = useSelector(activeNetworkSelector);
  const activeNetwork = networkOverride || activeNetworkFromStore;

  // LinkedIn sync
  const linkedinSync = useMemo(
    () => ({
      syncStatus: user.linkedinSync?.syncStatus || 'not_connected',
      lastSyncedAt: user.linkedinSync?.lastSyncedAt,
      contactsSynced: user.linkedinSync?.contactsSynced || 0,
    }),
    [user.linkedinSync],
  );
  const [updateLinkedinSyncStatus, { isLoading: isUpdatingLinkedinSync }] = useUpdateLinkedinSyncStatusMutation();
  const [disconnectLinkedinSync, { isLoading: isDisconnectingLinkedin }] = useDisconnectLinkedinSyncMutation();

  // Google sync
  const [createGoogleAccount] = useCreateUserGoogleAccountMutation();
  const [deleteGoogleAccount, { isLoading: isDeletingGoogleAccount }] = useDeleteUserGoogleAccountMutation();
  const { googleSync, refetch: refetchGoogleSync } = useGoogleSync();
  const [getGoogleAccountsStatus] = useLazyGetUserGoogleAccountsQuery();
  const pollingRef = useRef(null);

  // Poll for Google sync status when syncing
  useEffect(() => {
    let pendingQuery = null;

    const cleanup = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pendingQuery?.abort();
    };

    if (googleSync.syncStatus !== 'syncing') {
      cleanup();
      return () => {};
    }

    pollingRef.current = setInterval(() => {
      pendingQuery = getGoogleAccountsStatus();
      pendingQuery.then((res) => {
        const accounts = res?.data || [];
        const account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
        if (account?.syncStatus && account.syncStatus !== 'syncing') {
          refetchGoogleSync();
          cleanup();
        }
      });
    }, 3000);

    return cleanup;
  }, [googleSync.syncStatus, getGoogleAccountsStatus, refetchGoogleSync]);

  const onGoogleSuccess = async ({ code }) => {
    try {
      await createGoogleAccount({ code }).unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'google-account-connected',
      });
      onGoogleSyncComplete?.();
    } catch (error) {
      const message = error?.data?.error || 'Failed to connect Google account';
      toast(message, { icon: 'error' });
    }
  };

  const onGoogleError = (error) => {
    let message = 'Failed to start Google authorization';

    if (error?.error === 'access_denied') {
      message =
        'Google authorization was cancelled. Please try again and grant the required permissions to sync your contacts.';
    } else if (error?.error) {
      message = error.error;
    }

    toast(message, { icon: 'error' });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError: onGoogleError,
    flow: 'auth-code',
    redirect_uri: 'postmessage',
    scope: 'https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/contacts.other.readonly',
  });

  const getLinkedinSyncLink = useCallback(
    (status = 'sync') =>
      user.isOnlyAlly || !activeNetwork?.slug
        ? `/linkedin/${status}`
        : `/networks/${activeNetwork.slug}/linkedin/${status}`,
    [activeNetwork?.slug, user.isOnlyAlly],
  );

  const handleConfirmRemoveLinkedin = useCallback(async () => {
    try {
      await disconnectLinkedinSync().unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'linkedin-sync-disconnected',
      });
    } catch (error) {
      const message = error?.data?.error || 'Failed to disconnect LinkedIn sync';
      toast(message, { icon: 'error' });
    }
  }, [disconnectLinkedinSync]);

  const handleConfirmRemoveGoogle = useCallback(async () => {
    try {
      if (!googleSync?.id) return;
      await deleteGoogleAccount({ id: googleSync.id }).unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'google-account-removed',
      });
    } catch (error) {
      const message = error?.data?.error || 'Failed to remove Google account';
      toast(message, { icon: 'error' });
    }
  }, [deleteGoogleAccount, googleSync?.id]);

  return {
    user,
    activeNetwork,
    googleSync,
    googleLogin,
    getLinkedinSyncLink,
    handleConfirmRemoveGoogle,
    handleConfirmRemoveLinkedin,
    isDisconnectingLinkedin,
    isDeletingGoogleAccount,
    linkedinSync,
    isUpdatingLinkedinSync,
    updateLinkedinSyncStatus,
  };
};
